import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { PointsBadge } from '@/components/PointsBadge'
import type {
  FootballMatch,
  PouleActivityFeed,
  PouleDashboardBootstrapResponse,
  PouleInfoResponse,
  PouleRoundWinner,
} from '@/features/poules/api'
import { usePouleActivities } from '@/features/poules/api'
import { PouleBadgesCard } from '@/features/poules/components/PouleBadgesCard'
import { PouleDashboardRoundRankingCard } from '@/features/poules/components/PouleDashboardRoundRankingCard'
import { canPredictMatch } from '@/features/poules/components/PouleMatchesTab'
import { translate, useTranslation } from '@/i18n'
import { getLocale, toIntlLocale } from '@/config/locale'

export function PouleDashboardTab({
  pouleId,
  bootstrap,
  liveMatches,
  liveMatchesState,
  upcomingMatches,
  upcomingMatchesState,
  onSelectUpcomingMatch,
  onOpenRankingTab,
  memberCount,
}: {
  pouleId: number
  bootstrap: PouleDashboardBootstrapResponse
  liveMatches: FootballMatch[]
  liveMatchesState: 'idle' | 'loading' | 'error' | 'ready'
  upcomingMatches: FootballMatch[]
  upcomingMatchesState: 'idle' | 'loading' | 'error' | 'ready'
  onSelectUpcomingMatch: (match: FootballMatch) => void
  onOpenRankingTab?: () => void
  memberCount?: PouleInfoResponse['member_count']
}) {
  const [isLiveOpen, setIsLiveOpen] = useState(false)
  const { t } = useTranslation()
  const dashboard = bootstrap.dashboard
  const predictableMatches = upcomingMatches.filter(canPredictMatch)
  const hasLiveMatches = liveMatchesState === 'ready' && liveMatches.length > 0
  const hasUpcomingMatches = upcomingMatchesState === 'ready' && predictableMatches.length > 0

  return (
    <div className="space-y-6">
      {hasLiveMatches ? (
        <LiveMatchesTile count={liveMatches.length} onClick={() => setIsLiveOpen(true)} />
      ) : null}

      {hasUpcomingMatches ? (
        <UpcomingMatchesTile
          count={predictableMatches.length}
          onClick={() => {
            const nextMatch = predictableMatches[0]
            if (nextMatch) onSelectUpcomingMatch(nextMatch)
          }}
        />
      ) : null}

      <section className="space-y-3">
        <p className="px-1 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
          {t('Mijn stats')}
        </p>
        <div className="grid grid-cols-2 items-stretch gap-3">
          <PouleDashboardRoundRankingCard pouleId={pouleId} dashboard={dashboard} />
          <MyStandingCard
            standing={dashboard?.standing}
            memberCount={memberCount}
            onClick={onOpenRankingTab}
          />
        </div>
      </section>

      <RoundWinnerCard winner={bootstrap.round_winner} />
      <ActivitiesCard pouleId={pouleId} initialActivities={bootstrap.activities} />
      <PouleBadgesCard pouleId={pouleId} previewBadges={bootstrap.recent_badges_compact ?? []} />

      {isLiveOpen ? (
        <LiveMatchesOverlay
          pouleId={pouleId}
          matches={liveMatches}
          onClose={() => setIsLiveOpen(false)}
        />
      ) : null}
    </div>
  )
}

function LiveMatchesTile({ count, onClick }: { count: number; onClick: () => void }) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] px-5 py-4 text-left transition hover:border-surface-600 hover:bg-[#151a1c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {t('Live wedstrijden')}
        </p>
        <p className="mt-2 text-sm font-semibold text-surface-200">
          {t('Live bezig')}
        </p>
      </div>
      <span className="inline-flex min-w-8 items-center justify-center rounded-full bg-red-500 px-2.5 py-1 text-sm font-black text-white">
        {count}
      </span>
      <span aria-hidden="true" className="text-2xl leading-none text-surface-400">→</span>
    </button>
  )
}

function UpcomingMatchesTile({ count, onClick }: { count: number; onClick: () => void }) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[1.2rem] border border-white/[0.07] bg-[#111719] p-4 sm:p-5">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white">{t('Wedstrijden voorspellen')}</p>
          <p className="mt-1 text-sm text-surface-400">{t('Nog {count} wedstrijden open', { count })}</p>
        </div>
        <button
          type="button"
          onClick={onClick}
          className="group inline-flex shrink-0 items-center gap-2 rounded-xl bg-accent-600 px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111719]"
        >
          <span>{t('Voorspellen')}</span>
          <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none">
            <path d="M4 10h11m-4-4 4 4-4 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
          </svg>
        </button>
      </div>
    </section>
  )
}

function MyStandingCard({
  standing,
  memberCount,
  onClick,
}: {
  standing?: { rank?: number | null | undefined; total_users?: number | null | undefined } | null | undefined
  memberCount?: number | undefined
  onClick?: (() => void) | undefined
}) {
  const { t } = useTranslation()
  const rank = typeof standing?.rank === 'number' ? standing.rank : null
  const total =
    typeof standing?.total_users === 'number'
      ? standing.total_users
      : typeof memberCount === 'number'
        ? memberCount
        : null

  const content = (
    <div className="h-full rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] p-5 text-left transition hover:border-surface-600 hover:bg-[#151a1c]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
        {t('Mijn positie')}
      </p>
      <div className="mt-3 flex items-end gap-2">
        <span className="text-3xl font-black tracking-tight text-white">{rank ?? '–'}</span>
        {total != null ? <span className="pb-1 text-sm font-medium text-surface-400">{t('van {count}', { count: total })}</span> : null}
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 text-xs font-semibold text-surface-400">
        <span className="whitespace-nowrap">{onClick ? t('Stand bekijken') : t('In deze poule')}</span>
        {onClick ? <span aria-hidden="true" className="text-lg">→</span> : null}
      </div>
    </div>
  )

  return onClick ? (
    <button type="button" onClick={onClick} className="block h-full w-full text-left">{content}</button>
  ) : content
}

function RoundWinnerCard({ winner }: { winner: PouleRoundWinner | null | undefined }) {
  const { t } = useTranslation()
  const winnerName = findFirstString(winner, ['display_name', 'name', 'title'])
  if (!winnerName) return null

  const points = findFirstNumber(winner, ['points', 'score', 'total_points'])
  const round = findFirstString(winner, ['round_name', 'round', 'subtitle'])

  return (
    <section className="rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">{t('Rondewinnaar')}</p>
      <div className="mt-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate text-lg font-black text-white">{winnerName}</p>
          {round ? <p className="mt-1 text-sm text-surface-400">{round}</p> : null}
        </div>
        {points != null ? <PointsBadge points={points} /> : null}
      </div>
    </section>
  )
}

function ActivitiesCard({
  pouleId,
  initialActivities,
}: {
  pouleId: number
  initialActivities: PouleActivityFeed | null | undefined
}) {
  const { t } = useTranslation()
  const shouldUseFallback = initialActivities == null
  const { data: fallbackActivities, isError, isLoading } = usePouleActivities(pouleId, {
    limit: 20,
    enabled: shouldUseFallback,
  })
  const activities = initialActivities ?? fallbackActivities
  const source = initialActivities != null ? 'bootstrap' : fallbackActivities != null ? 'fallback /activities' : null
  const items = activities?.items ?? []

  useEffect(() => {
    if (!import.meta.env.DEV || !source) return
    console.info('[poule-activities] source', source)
  }, [source])

  if (shouldUseFallback && isLoading && items.length === 0) {
    return <section className="h-48 animate-pulse rounded-[1.25rem] bg-[#0d1113]" aria-label={t('Activiteit laden…')} />
  }

  if (isError && items.length === 0) {
    return <section className="rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] p-5 text-sm text-red-200">{t('Activiteit kon niet worden geladen.')}</section>
  }

  if (items.length === 0) return null

  return (
    <section>
      <div className="mb-3 flex items-center gap-2 px-1">
        <span className="h-2 w-2 rounded-full bg-[#63e6be]" aria-hidden="true" />
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-surface-300">{t('Poule highlights')}</p>
      </div>
      <div className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 touch-pan-x">
        {items.slice(0, 3).map((item, index) => (
          <ActivitySlide key={`${String(item.id ?? item.uuid ?? index)}`} item={item} />
        ))}
      </div>
      {items.length > 1 ? (
        <div className="mt-1 flex justify-center gap-1.5" aria-hidden="true">
          {items.slice(0, 3).map((_, index) => (
            <span key={index} className={`h-1.5 rounded-full ${index === 0 ? 'w-4 bg-white/70' : 'w-1.5 bg-white/20'}`} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function ActivitySlide({ item }: { item: Record<string, unknown> }) {
  const { t } = useTranslation()
  const actor = asRecord(item.actor)
  const actorName = findFirstString(actor, ['display_name', 'name', 'username'])
  const image = findFirstString(actor, ['profile_image', 'avatar', 'image'])
  const text = getActivityText(item, t)
  const timestamp = formatActivityTime(findFirstString(item, ['published_at', 'created_at', 'time_ago']), getLocale())

  return (
    <article className="w-[86%] shrink-0 snap-start rounded-[1.15rem] border border-white/[0.06] bg-[#0d1113] p-4 sm:w-[24rem]">
      <div className="flex items-start gap-3">
        <ActivityAvatar name={actorName || text} image={image} />
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-white">{actorName || text}</p>
          {timestamp ? <p className="mt-1 text-xs font-semibold text-surface-500">{timestamp}</p> : null}
        </div>
      </div>
      <p className="mt-4 line-clamp-3 text-sm leading-6 text-surface-300">{text}</p>
    </article>
  )
}

function ActivityAvatar({ name, image }: { name: string; image?: string | undefined }) {
  if (image) return <img src={image} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
  return <span className="poule-activity-avatar flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#182126] text-sm font-black uppercase text-surface-300">{getInitials(name)}</span>
}

function LiveMatchesOverlay({
  pouleId,
  matches,
  onClose,
}: {
  pouleId: number
  matches: FootballMatch[]
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <div className="animate-drawer-backdrop-in fixed inset-0 z-[72] bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="animate-manager-player-picker-in fixed inset-x-0 bottom-0 z-[73] mx-auto max-h-[88vh] w-full max-w-3xl overflow-hidden rounded-t-[1.5rem] border border-white/[0.08] bg-[#0d1113] shadow-[0_-24px_72px_rgba(0,0,0,0.4)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#202427] px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">{t('Live')}</p>
            <h2 className="mt-1 text-xl font-black text-white">{t('Live wedstrijden')}</h2>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div className="max-h-[calc(88vh-5rem)] space-y-3 overflow-y-auto p-5">
          {matches.map((match) => <LiveMatchRow key={match.id} pouleId={pouleId} match={match} />)}
        </div>
      </div>
    </>
  )
}

function LiveMatchRow({ match, pouleId }: { match: FootballMatch; pouleId: number }) {
  const { t } = useTranslation()
  return (
    <Link
      to={getLiveMatchDetailPath(match, pouleId)}
      className="block rounded-[1rem] border border-[#4a1625] bg-[#170d12] px-4 py-3 transition hover:border-red-400/50 hover:bg-[#211017] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
      aria-label={`${match.home_team.name} - ${match.away_team.name}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-14 shrink-0 text-center">
          <span className="inline-flex rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-red-200">{t('Live')}</span>
          <p className="mt-1 text-[11px] font-semibold text-white">{getMatchStatus(match)}</p>
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <LiveTeamRow team={match.home_team} score={match.score?.home} />
          <LiveTeamRow team={match.away_team} score={match.score?.away} />
        </div>
        <span className="shrink-0 text-sm font-black text-white">{getMatchScore(match)}</span>
      </div>
    </Link>
  )
}

function getLiveMatchDetailPath(match: FootballMatch, pouleId: number) {
  const params = new URLSearchParams({ sourceTab: 'dashboard' })
  if (typeof match.poule_match_id === 'number') {
    params.set('pouleMatchId', String(match.poule_match_id))
  }

  return `/poules/${pouleId}/wedstrijden/${match.id}?${params.toString()}`
}

function LiveTeamRow({ team, score, pointsBadgeValue }: { team: FootballMatch['home_team']; score?: number | null | undefined; pointsBadgeValue?: number }) {
  return (
    <div className="flex items-center gap-2">
      {team.logo_url ? <img src={team.logo_url} alt="" className="h-7 w-7 object-contain" /> : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-800 text-[10px] font-black text-surface-300">{getInitials(team.name)}</span>}
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{team.name}</span>
      {typeof pointsBadgeValue === 'number' ? <PointsBadge points={pointsBadgeValue} /> : null}
      <span className="font-black text-white">{typeof score === 'number' ? score : '–'}</span>
    </div>
  )
}

function getActivityText(item: Record<string, unknown>, t: (key: string) => string) {
  const localizedText = asRecord(item.text)

  return findFirstString(localizedText, ['value', 'fallback_text'])
    || findFirstString(item, ['display_text', 'fallback_text', 'title'])
    || findFirstString(localizedText, ['label', 'key'])
    || t('Nieuwe activiteit')
}

function formatActivityTime(value: string | undefined, locale: string) {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(toIntlLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function findFirstString(payload: Record<string, unknown> | null | undefined, keys: string[]) {
  for (const key of keys) {
    const value = payload?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function findFirstNumber(payload: Record<string, unknown> | null | undefined, keys: string[]) {
  for (const key of keys) {
    const value = payload?.[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }
  return undefined
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function getInitials(value: string) {
  return value.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

function getMatchStatus(match: FootballMatch) {
  if (typeof match.minute === 'number') {
    return typeof match.extra_minute === 'number' ? `${match.minute}+${match.extra_minute}'` : `${match.minute}'`
  }
  if (match.status === 'ft') return translate('Eindstand')
  if (match.status === 'ht') return translate('Rust')
  if (match.status === 'cancelled') return translate('Geannuleerd')
  if (match.status === 'postponed') return translate('Uitgesteld')
  return match.status === 'live' || !match.status ? translate('Live') : translate('Gepland')
}

function getMatchScore(match: FootballMatch) {
  const home = match.score?.home
  const away = match.score?.away
  return typeof home === 'number' && typeof away === 'number' ? `${home} - ${away}` : '–'
}
